import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Car, 
  Building2, 
  Briefcase, 
  Truck, 
  Shield,
  Phone,
  Mail,
  MapPin,
  Clock,
  ChevronRight,
  Wrench,
  FileCheck,
  Home as HomeIcon,
  Users,
  Sparkles,
  Send
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import LanguageSwitcher from '../components/LanguageSwitcher';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('services');
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [reservationForm, setReservationForm] = useState({ name: '', email: '', phone: '', service: '', date: '', notes: '' });
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const productsServices = {
    products: [
      { 
        title: 'Cement i Malteri', 
        description: 'Širok asortiman cementa i gotovih maltera',
        image: 'https://images.unsplash.com/photo-1560435650-7ec2e17ba926?w=600&h=400&fit=crop',
        icon: <Building2 className="w-6 h-6" />,
        details: {
          fullDescription: 'Nudimo visokokvalitetne cemente i gotove maltere za sve vrste građevinskih radova. Naši proizvodi zadovoljavaju najviše standarde kvalitete i pogodni su za profesionalnu i privatnu upotrebu.',
          features: [
            'Portland cement CEM I 42.5R',
            'Portland cement CEM II/A-S 42.5N',
            'Univerzalni malter za zidanje',
            'Ljepilo za keramičke pločice',
            'Malter za izravnavanje',
            'Fasadni malter'
          ],
          specifications: 'Prema HRN EN 197-1 standardu. Pakiranje: vreće od 25kg i 40kg. Rok trajanja: 6-12 mjeseci.',
          applications: 'Betonski radovi, zidanje, žbukanje, fugiranje, popravci, izolacija'
        }
      },
      { 
        title: 'Metalni Profili', 
        description: 'Široka paleta metalnih profila za različite namjene',
        image: 'https://images.unsplash.com/photo-1593012672078-abaa5ef8acc0?w=600&h=400&fit=crop',
        icon: <Wrench className="w-6 h-6" />,
        details: {
          fullDescription: 'Proizvođimo i distribuiramo širok asortiman metalnih profila različitih dimenzija i kvaliteta čelika. Svi profili su izrađeni prema europskim standardima.',
          features: [
            'IPE nosači (80-600mm)',
            'HEB nosači (100-1000mm)',
            'UNP profili',
            'Kutni profili (L profili)',
            'Kvadratne cijevi',
            'Pravokutne cijevi'
          ],
          specifications: 'Materijal: S235JR, S355JR. Površinska obrada: valjano, galvanizirano, lakirano. Dužine: standardne 6m, 12m ili po narudžbi.',
          applications: 'Čelične konstrukcije, hale, nadvožnjaci, potporni stupovi, ograde'
        }
      },
      { 
        title: 'Armirani Beton', 
        description: 'Visokokvalitetan armirani beton za sve vrste građevinskih projekata',
        image: 'https://images.unsplash.com/photo-1616614979687-8a8fece9cfb2?w=600&h=400&fit=crop',
        icon: <Building2 className="w-6 h-6" />,
        details: {
          fullDescription: 'Isporučujemo svježi armirani beton svih klasa čvrstoće. Miješalica za beton je opremljena najsuvremenijom tehnologijom za precizno doziranje.',
          features: [
            'Beton C16/20 - C50/60',
            'Vodonepropusni beton',
            'Laki beton',
            'Samougrađujući beton',
            'Pumpni beton',
            'Brzotvrdnući beton'
          ],
          specifications: 'Prema HRN EN 206 standardu. Isporuka: autobetonskim miješalicama 6-10m³. Vrijeme isporuke: 60-90 minuta od narudžbe.',
          applications: 'Temelji, stupovi, grede, ploče, zidovi, mostovi, tuneli'
        }
      },
      { 
        title: 'Čelične Konstrukcije', 
        description: 'Proizvodnja čeličnih konstrukcija po najnovijim standardima',
        image: 'https://images.unsplash.com/uploads/14123892966835548e7bd/14369636?w=600&h=400&fit=crop',
        icon: <Shield className="w-6 h-6" />,
        details: {
          fullDescription: 'Projektiramo, proizvodimo i montiramo čelične konstrukcije za industrijske i komercijalne objekte. Sva izrada se odvija u našoj modernoj proizvodnoj hali.',
          features: [
            'Čelične hale (do 30m raspona)',
            'Krovne konstrukcije',
            'Mostovske konstrukcije',
            'Platforme i podesti',
            'Protupožarne konstrukcije',
            'Stepeništa i ograde'
          ],
          specifications: 'Prema Eurokod 3 standardu. Zaštita: vatrostalna i antikorozivna. Montaža: s vlastitom opremom i iskusnim timom.',
          applications: 'Industrijske hale, proizvodni pogoni, skladišta, sportske dvorane, izložbeni prostori'
        }
      },
      { 
        title: 'Građevinska Opeka', 
        description: 'Kvalitetna opeka za zidanje i fasadne radove',
        image: 'https://images.unsplash.com/photo-1629608564457-5d74829a9e14?w=600&h=400&fit=crop',
        icon: <Building2 className="w-6 h-6" />,
        details: {
          fullDescription: 'Nudimo kvalitetnu glinenu opeku i blokove za sve vrste zidanja. Naši proizvodi odlikuju se izvrsnim termo-izolacijskim svojstvima i trajnošću.',
          features: [
            'Šuplja opeka (25cm, 29cm)',
            'Fasadna opeka (razne boje)',
            'Porotherm blokovi',
            'Ytong blokovi',
            'Opločnici (za terase)',
            'Crijep'
          ],
          specifications: 'Prema HRN EN 771-1 standardu. Dimenzije: standardne i atipične. Toplinska provodljivost: λ = 0.09-0.45 W/mK.',
          applications: 'Nosivi zidovi, pregradni zidovi, fasade, dimnjaci, ograde'
        }
      },
      { 
        title: 'Armaturna Mreža', 
        description: 'Različite dimenzije armaturnih mreža za ojačavanje betona',
        image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=600&h=400&fit=crop',
        icon: <Wrench className="w-6 h-6" />,
        details: {
          fullDescription: 'Proizvodimo armaturne mreže prema projektnoj dokumentaciji. Sve mreže su izrađene od rebrastog čelika visoke kvalitete s automatiziranim zavarivanjem.',
          features: [
            'Q mreže (Q196, Q257, Q335, Q385)',
            'MAG mreže',
            'Mreže po narudžbi',
            'Betonski distanceri',
            'Armaturne šipke φ6-φ32',
            'Vezica za armaturu'
          ],
          specifications: 'Prema HRN EN 10080 standardu. Dimenzije: standardne 6x2.15m ili po narudžbi. Materijal: B500B rebrasti čelik.',
          applications: 'Armiranje betonskih ploča, temelja, zidova, kolnika, parkirališta'
        }
      },
      { 
        title: 'Auto Dijelovi', 
        description: 'Širok asortiman auto dijelova za sve marke vozila',
        image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600&h=400&fit=crop',
        icon: <Car className="w-6 h-6" />,
        details: {
          fullDescription: 'Distributer smo originalnih i zamjenskih auto dijelova za sve marke i modele osobnih vozila. Imamo vlastito skladište s preko 50.000 artikala.',
          features: [
            'Dijelovi motora i transmisije',
            'Dijelovi ovjesa i upravljanja',
            'Kočnice i clutch sistemi',
            'Električni i elektronički dijelovi',
            'Filtri, ulja i tekućine',
            'Dijelovi karoserije'
          ],
          specifications: 'Originalni i OEM kvaliteta. Garancija: 12-24 mjeseca. Dostava: 24-48h za dijelove na lageru. Podrška: tehnička pomoć pri odabiru.',
          applications: 'Servisne radionice, automehaničari, privatni kupci'
        }
      },
      { 
        title: 'Dijelovi za Teretna Vozila', 
        description: 'Kvalitetni rezervni dijelovi za kamione i teretna vozila',
        image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&h=400&fit=crop',
        icon: <Truck className="w-6 h-6" />,
        details: {
          fullDescription: 'Specijalizirani smo za rezervne dijelove za teška teretna vozila svih vodećih proizvođača. Nudimo brzu dostavu i stručnu podršku.',
          features: [
            'Dijelovi motora (MAN, Scania, Volvo, Mercedes)',
            'Mjenjačke komponente',
            'Osovine i kardansko vratilo',
            'Pneumatski sustavi',
            'Dijelovi kočionog sistema',
            'Hidraulika i priključnice'
          ],
          specifications: 'Originalni i aftermarket dijelovi. Garancija: 12-36 mjeseci. Express dostava za hitne slučajeve. Tehnička podrška 24/7.',
          applications: 'Fleet operatori, servisi za teretna vozila, prijevoznička poduzeća'
        }
      }
    ]
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast({
        title: "Poruka poslana!",
        description: "Hvala na vašoj poruci. Kontaktirat ćemo vas uskoro.",
      });
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast({
        title: "Greška",
        description: "Nešto je pošlo po zlu. Molimo pokušajte ponovo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/reservations`, reservationForm);
      toast({
        title: "Rezervacija poslana!",
        description: "Vaša rezervacija je uspješno zaprimljena. Kontaktirat ćemo vas uskoro.",
      });
      setReservationForm({ name: '', email: '', phone: '', service: '', date: '', notes: '' });
      setSelectedService(null);
    } catch (error) {
      toast({
        title: "Greška",
        description: "Nešto je pošlo po zlu. Molimo pokušajte ponovo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReservation = (service) => {
    setSelectedService(service);
    setReservationForm({ ...reservationForm, service: service.title });
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Language Switcher */}
      <LanguageSwitcher />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-32 px-4 overflow-hidden">
        {/* Geometric construction-themed background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Construction crane lines */}
          <svg className="absolute top-20 right-10 w-96 h-96 opacity-10" viewBox="0 0 200 200">
            <line x1="100" y1="0" x2="100" y2="100" stroke="#ef4444" strokeWidth="2"/>
            <line x1="100" y1="20" x2="180" y2="20" stroke="#ef4444" strokeWidth="2"/>
            <rect x="160" y="15" width="25" height="10" fill="#ef4444"/>
            <line x1="90" y1="100" x2="110" y2="100" stroke="#9ca3af" strokeWidth="4"/>
          </svg>
          
          {/* Building silhouettes */}
          <div className="absolute bottom-0 left-0 right-0 opacity-5">
            <svg viewBox="0 0 1200 300" className="w-full">
              <rect x="50" y="150" width="80" height="150" fill="#9ca3af"/>
              <rect x="150" y="100" width="60" height="200" fill="#ef4444"/>
              <rect x="230" y="120" width="90" height="180" fill="#9ca3af"/>
              <rect x="340" y="80" width="70" height="220" fill="#ef4444"/>
              <rect x="430" y="140" width="85" height="160" fill="#9ca3af"/>
              <polygon points="500,50 550,100 600,50 600,300 500,300" fill="#ef4444"/>
              <rect x="620" y="110" width="75" height="190" fill="#9ca3af"/>
              <rect x="710" y="90" width="65" height="210" fill="#ef4444"/>
              <rect x="790" y="130" width="80" height="170" fill="#9ca3af"/>
            </svg>
          </div>
          
          {/* Curved arc lines inspired by logo */}
          <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-20" viewBox="0 0 1200 800">
            <path d="M 200 400 Q 600 200, 1000 400" stroke="#ef4444" strokeWidth="3" fill="none"/>
            <path d="M 200 420 Q 600 220, 1000 420" stroke="#9ca3af" strokeWidth="3" fill="none"/>
          </svg>
          
          {/* Animated floating circles */}
          <div className="absolute top-20 right-20 w-96 h-96 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gray-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16 animate-fade-in">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pi-transport/artifacts/77urlfbx_ChatGPT%20Image%2010.%20tra%202026.%2021_02_37.png" 
                  alt="PI AUTO Logo" 
                  className="w-auto h-80 md:h-[28rem] lg:h-[32rem] object-contain animate-float"
                  style={{
                    filter: 'drop-shadow(0 25px 50px rgba(239, 68, 68, 0.5))',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
                    maskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)'
                  }}
                />
              </div>
            </div>
            
            <div className="h-1 w-32 bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto mb-8"></div>
            <p className="text-2xl md:text-3xl text-gray-100 max-w-3xl mx-auto font-light leading-relaxed">
              {t('hero.tagline')}
            </p>
            <p className="text-lg text-gray-300 mt-6 font-light">
              {t('hero.subtitle')}
            </p>
            <Button 
              onClick={scrollToContact}
              size="lg" 
              className="mt-10 bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 transition-all duration-500 transform hover:scale-110 shadow-2xl hover:shadow-red-500/50 border-0 px-10 py-6 text-lg font-semibold rounded-full shine-effect"
            >
              {t('hero.contactButton')}
            </Button>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 gap-8 mt-20">
            <Card className="glass-effect shadow-2xl hover:shadow-red-500/30 transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 border-gray-700 hover:border-red-500 group shine-effect">
              <CardHeader className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-6 text-white shadow-xl group-hover:shadow-red-500/50 transition-all duration-500 group-hover:rotate-6 animate-glow">
                  <Building2 className="w-10 h-10" />
                </div>
                <CardTitle className="text-2xl text-white group-hover:text-red-400 transition-colors duration-300 font-bold">Građevinski Materijali</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-lg leading-relaxed">
                  Visokokvalitetni građevinski materijali za sve vrste projekata
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="glass-effect shadow-2xl hover:shadow-red-500/30 transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 border-gray-700 hover:border-red-500 group shine-effect">
              <CardHeader className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-6 text-white shadow-xl group-hover:shadow-red-500/50 transition-all duration-500 group-hover:rotate-6 animate-glow">
                  <Shield className="w-10 h-10" />
                </div>
                <CardTitle className="text-2xl text-white group-hover:text-red-400 transition-colors duration-300 font-bold">Profesionalna Usluga</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-lg leading-relaxed">
                  Stručan tim i garancija kvalitete
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-800 via-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-white">
              O <span className="text-red-500">Nama</span>
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
              PI AUTO j.d.o.o. je pouzdana tvrtka koja pruža širok spektar profesionalnih usluga. 
              S našim stručnim timom i dugogodišnjim iskustvom, nudimo kompletna rješenja - 
              od iznajmljivanja vozila do građevinskih materijala i usluga.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <Card className="glass-effect border-2 border-gray-700 hover:border-red-500 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/20 transform hover:-translate-y-2 group">
              <CardHeader className="text-center">
                <div className="text-6xl font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">15+</div>
                <CardTitle className="text-lg text-white font-semibold">Godina Iskustva</CardTitle>
              </CardHeader>
            </Card>
            <Card className="glass-effect border-2 border-gray-700 hover:border-red-500 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/20 transform hover:-translate-y-2 group">
              <CardHeader className="text-center">
                <div className="text-6xl font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">100%</div>
                <CardTitle className="text-lg text-white font-semibold">Zadovoljstvo Klijenata</CardTitle>
              </CardHeader>
            </Card>
            <Card className="glass-effect border-2 border-gray-700 hover:border-red-500 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/20 transform hover:-translate-y-2 group">
              <CardHeader className="text-center">
                <div className="text-6xl font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">24/7</div>
                <CardTitle className="text-lg text-white font-semibold">Dostupnost</CardTitle>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-800 via-gray-900 to-gray-900 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-white">
              Naši <span className="text-red-500">Proizvodi</span>
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">
              Kompletna ponuda za sve vaše potrebe
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productsServices.products.map((item, index) => (
              <Dialog key={index}>
                <DialogTrigger asChild>
                  <Card 
                    className="group glass-effect hover:shadow-2xl transition-all duration-500 border-2 border-gray-700 hover:border-red-500 cursor-pointer transform hover:-translate-y-3 hover:scale-105 overflow-hidden shine-effect"
                    style={{animationDelay: `${index * 0.1}s`}}
                    onClick={() => setSelectedProduct(item)}
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-125 group-hover:rotate-3"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                      <div className="absolute top-4 right-4 w-12 h-12 bg-red-600/80 backdrop-blur rounded-full flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300">
                        {item.icon}
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl text-white group-hover:text-red-400 transition-colors duration-300 font-bold">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-400 mb-6 text-base leading-relaxed">
                        {item.description}
                      </CardDescription>
                      <div className="flex items-center text-red-500 text-sm font-semibold group-hover:text-red-400 group-hover:gap-3 transition-all duration-300">
                        Klikni za više detalja <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                
                <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-3xl text-white mb-4 flex items-center gap-3">
                      <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                        {item.icon}
                      </div>
                      {item.title}
                    </DialogTitle>
                  </DialogHeader>
                  
                  {item.details && (
                    <div className="space-y-6 mt-4">
                      {/* Image */}
                      <div className="relative h-64 rounded-lg overflow-hidden">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                      </div>
                      
                      {/* Description */}
                      <div>
                        <h3 className="text-xl font-bold text-red-400 mb-3">Opis</h3>
                        <p className="text-gray-300 leading-relaxed text-lg">
                          {item.details.fullDescription}
                        </p>
                      </div>
                      
                      {/* Features */}
                      <div>
                        <h3 className="text-xl font-bold text-red-400 mb-3">Asortiman</h3>
                        <ul className="grid md:grid-cols-2 gap-3">
                          {item.details.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-300">
                              <ChevronRight className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Specifications */}
                      <div>
                        <h3 className="text-xl font-bold text-red-400 mb-3">Specifikacije</h3>
                        <p className="text-gray-300 leading-relaxed bg-gray-900/50 p-4 rounded-lg">
                          {item.details.specifications}
                        </p>
                      </div>
                      
                      {/* Applications */}
                      <div>
                        <h3 className="text-xl font-bold text-red-400 mb-3">Primjena</h3>
                        <p className="text-gray-300 leading-relaxed">
                          {item.details.applications}
                        </p>
                      </div>
                      
                      {/* Contact CTA */}
                      <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-lg mt-6">
                        <h3 className="text-xl font-bold text-white mb-2">Zainteresirani?</h3>
                        <p className="text-white/90 mb-4">Kontaktirajte nas za dodatne informacije, ponudu ili narudžbu.</p>
                        <Button 
                          onClick={scrollToContact}
                          className="bg-white text-red-600 hover:bg-gray-100 font-semibold"
                        >
                          Kontaktirajte nas
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-red-900 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Kontakt</h2>
            <p className="text-lg text-gray-300">
              Tu smo da odgovorimo na sva vaša pitanja
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="bg-gray-800/50 backdrop-blur border-gray-700 hover:bg-gray-800/70 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/20">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-red-400" />
                  </div>
                  <CardTitle className="text-white text-lg">Telefon</CardTitle>
                </CardHeader>
                <CardContent>
                  <a href="tel:+385919871975" className="text-gray-300 hover:text-red-400 transition-colors text-lg block mb-2">
                    +385 91 987 1975
                  </a>
                  <a href="tel:+385919567773" className="text-gray-300 hover:text-red-400 transition-colors text-lg block">
                    +385 91 956 7773
                  </a>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 backdrop-blur border-gray-700 hover:bg-gray-800/70 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/20">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-red-400" />
                  </div>
                  <CardTitle className="text-white text-lg">Email</CardTitle>
                </CardHeader>
                <CardContent>
                  <a href="mailto:info@pi-auto.eu" className="text-gray-300 hover:text-red-400 transition-colors break-all block mb-2">
                    info@pi-auto.eu
                  </a>
                  <a href="mailto:matej@pi-auto.eu" className="text-gray-300 hover:text-red-400 transition-colors break-all block">
                    matej@pi-auto.eu
                  </a>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 backdrop-blur border-gray-700 hover:bg-gray-800/70 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/20">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-red-400" />
                  </div>
                  <CardTitle className="text-white text-lg">Adresa</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Ulica Vida Ročića 140</p>
                  <p className="text-gray-300">10000 Zagreb</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 backdrop-blur border-gray-700 hover:bg-gray-800/70 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/20">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-red-400" />
                  </div>
                  <CardTitle className="text-white text-lg">Radno vrijeme</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Pon - Pet: 08:00 - 16:00</p>
                  <p className="text-gray-300">Sub: 09:00 - 13:00</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="bg-gray-800/50 backdrop-blur border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Pošaljite nam poruku</CardTitle>
                <CardDescription className="text-gray-400">
                  Popunite obrazac i kontaktirat ćemo vas u najkraćem roku
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-white">Ime i Prezime</Label>
                    <Input 
                      id="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                      placeholder="Vaše ime"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white">Email</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                      placeholder="vas.email@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white">Telefon</Label>
                    <Input 
                      id="phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      required
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                      placeholder="+385 123 456 789"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-white">Poruka</Label>
                    <Textarea 
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={4}
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 resize-none"
                      placeholder="Vaša poruka..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white transition-all duration-300 transform hover:scale-105"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin">⏳</span> Šalje se...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" /> Pošalji Poruku
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="bg-gray-800/50 backdrop-blur border-gray-700 inline-block px-8">
              <CardHeader>
                <CardTitle className="text-white text-xl mb-2">PI AUTO j.d.o.o.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-gray-300">Ulica Vida Ročića 140, Zagreb</p>
                <p className="text-gray-300">OIB: <span className="font-mono">22663086541</span></p>
                <p className="text-gray-300">Email: <a href="mailto:info@pi-auto.eu" className="text-red-400 hover:text-red-300 transition-colors">info@pi-auto.eu</a></p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-gray-400 py-8 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} PI AUTO j.d.o.o. Sva prava pridržana.
          </p>
        </div>
      </footer>
      
      {/* Hide Emergent Badge */}
      <style dangerouslySetInnerHTML={{__html: `
        button[class*="fixed"],
        button[class*="bottom"],
        a[href*="emergent"],
        div[class*="Made"],
        [aria-label*="emergent" i] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}} />
    </div>
  );
};

export default Home;